import { HTTPError } from "ky";

import { readErrorDetail } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";

import {
  ServiceCreate,
  ServiceDetail,
  ServiceEdit,
  ServiceTable,
  VirtualServicesGuide,
} from "@/types/service";

import { requestData } from "@/api/api-config";

export async function listServices(params?: {
  ids?: string[];
  includeArchived?: boolean;
}): Promise<ServiceTable[]> {
  try {
    const services = await requestData<ServiceTable[]>(
      "admin/services/list",
      "get",
      params
        ? {
            searchParams: {
              ids: params.ids,
              ...(params.includeArchived !== undefined
                ? { include_archived: params.includeArchived }
                : {}),
            },
          }
        : undefined,
      false,
    );
    if (services === null) throw new Error("Services could not be loaded");
    return services;
  } catch (error) {
    toast({
      id: "services-load-error",
      title: "Failed to load services",
      description: "Please try again.",
      variant: "error",
    });
    throw error;
  }
}

export async function getService(
  serviceId: string,
): Promise<ServiceTable | null> {
  const services = await listServices({ ids: [serviceId] });
  return services[0] ?? null;
}

export async function deleteService(
  serviceId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `admin/services/delete/${serviceId}`,
    "delete",
  );
}

function buildServiceFormData(
  service: ServiceCreate | Partial<Omit<ServiceEdit, "id">>,
  imageFile: File,
): FormData {
  const formData = new FormData();
  formData.append("service", JSON.stringify(service));
  formData.append("image_file", imageFile);
  return formData;
}

interface ServiceSaveOptions {
  /** Throw the request's error, so the caller can show the server's reason. */
  throwOnError?: boolean;
}

export async function createService(
  service: ServiceCreate,
  imageFile?: File | null,
  { throwOnError = false }: ServiceSaveOptions = {},
): Promise<ServiceDetail | null> {
  if (imageFile) {
    return await requestData<ServiceDetail>(
      "admin/services/with-image",
      "post",
      {
        body: buildServiceFormData(service, imageFile),
      },
      !throwOnError,
    );
  }

  return await requestData<ServiceDetail>(
    "admin/services",
    "post",
    {
      json: service,
    },
    !throwOnError,
  );
}

/** Calendar Appearance's color-only save, open to Manage Calendar Appearance. */
export async function updateServiceColor(
  serviceId: string,
  color: string,
): Promise<ServiceDetail | null> {
  return await requestData<ServiceDetail>(
    `admin/services/${serviceId}/color`,
    "put",
    { json: { color } },
  );
}

export async function updateService(
  serviceId: string,
  service: Partial<Omit<ServiceEdit, "id">>,
  imageFile?: File | null,
  { throwOnError = false }: ServiceSaveOptions = {},
): Promise<ServiceDetail | null> {
  if (imageFile) {
    return await requestData<ServiceDetail>(
      `admin/services/${serviceId}/with-image`,
      "put",
      {
        body: buildServiceFormData(service, imageFile),
      },
      !throwOnError,
    );
  }

  return await requestData<ServiceDetail>(
    `admin/services/${serviceId}`,
    "put",
    {
      json: service,
    },
    !throwOnError,
  );
}

/** Whether to point the clinic to the virtual services guide, and why. */
export async function getVirtualServicesGuide(): Promise<VirtualServicesGuide | null> {
  return await requestData<VirtualServicesGuide>(
    "admin/services/virtual-services-guide",
    "get",
  );
}

/** Thrown when a direct save touches fields an approval policy governs. */
export class ApprovalRequiredError extends Error {
  constructor(readonly pendingFields: string[]) {
    super("These changes need approval.");
  }
}

/** `updateService`, except a save the API sends to approval throws `ApprovalRequiredError`. */
export async function updateServiceDirect(
  serviceId: string,
  service: Partial<Omit<ServiceEdit, "id">>,
  imageFile?: File | null,
  { throwOnError = false }: ServiceSaveOptions = {},
): Promise<ServiceDetail | null> {
  try {
    return await requestData<ServiceDetail>(
      imageFile
        ? `admin/services/${serviceId}/with-image`
        : `admin/services/${serviceId}`,
      "put",
      imageFile
        ? { body: buildServiceFormData(service, imageFile) }
        : { json: service },
      false,
    );
  } catch (error) {
    if (error instanceof HTTPError) {
      const detail = await readErrorDetail(error);
      if (detail?.code === "approval_required")
        throw new ApprovalRequiredError(detail.pendingFields ?? []);
    }
    if (throwOnError) throw error;
    console.error("Error fetching data:", error);
    return null;
  }
}

export async function bulkUpdateServices(
  updates: ServiceEdit[],
): Promise<ServiceDetail[]> {
  try {
    const result = await requestData<ServiceDetail[]>(
      "admin/services/bulk",
      "put",
      {
        json: { updates },
      },
      false,
    );
    if (result === null)
      throw new Error("Service updates were not saved. Please retry.");
    return result;
  } catch (error) {
    if (error instanceof HTTPError) {
      const detail = await readErrorDetail(error);
      if (typeof detail === "string") throw new Error(detail);
      if (detail?.code === "approval_required") {
        throw new Error(
          `Approval required for ${detail.pendingFields?.join(", ") || "these fields"}. Open each service to submit a change request.`,
        );
      }
    }
    throw error;
  }
}

export async function reorderServices(
  orders: { id: string; displayOrder: number }[],
): Promise<boolean | null> {
  return await requestData<boolean>("admin/services/reorder", "put", {
    json: {
      orders: orders.map((o) => ({
        id: o.id,
        display_order: o.displayOrder,
      })),
    },
  });
}
